/* GrowthOS — Price Sheet form sections (used by addPrice.jsx for Create/Edit).
   Config-driven on the shared gos-cl-* / gos-dd-* form primitives (same pattern as
   productForm/salesForm). Replaces the legacy Formik + SelectBox + Bootstrap grid.
   Handlers keep the exact container contract (handleChangeForm by name; handleChange
   ({value,label}, idKey) → handleLookupDropDownChange; handleChangeCheckbox). */
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getActiveOwners, getGenericMasterFilteredListByKey } from '../../redux/selector';
import { NameFormatter } from '../../shared/controls/formatters';
import { FIc } from "../../shared/component/GosIcon";


const CURRENCY = process.env.REACT_APP_CURRENCY || '$';

const Select = ({ value, placeholder, options, onChange, disabled, searchable }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const term = q.trim().toLowerCase();
  const shown = term ? options.filter((o) => String(o.label).toLowerCase().includes(term)) : options;
  return (
    <div className="gos-dd" ref={ref}>
      <button type="button" className={'gos-dd-btn' + (open ? ' open' : '') + (value ? ' has-val' : '')} onClick={() => !disabled && setOpen((o) => !o)} disabled={disabled}>
        <span className="gos-dd-label">{value || placeholder || 'Select…'}</span>
        <FIc name="chevD" size={14} style={{ flex: 'none', color: 'var(--gos-text-3)' }} />
      </button>
      {open ? (
        <div className="gos-dd-pop">
          {searchable ? (
            <div className="gos-dd-search">
              <FIc name="search" size={14} />
              <input autoFocus placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
          ) : null}
          <div className="gos-dd-list">
            {shown.length === 0 ? <div className="gos-dd-empty">No options</div> : null}
            {shown.map((o) => (
              <button key={o.value} type="button" className={'gos-dd-item' + (o.label === value ? ' on' : '')} onClick={() => { onChange(o); setOpen(false); setQ(''); }}>
                <span className="gos-dd-grow">{o.label}</span>
                {o.label === value ? <FIc name="check" size={14} style={{ color: 'var(--gos-accent)' }} /> : null}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const PriceForm = ({ formData, handleChangeForm, handleChange, handleChangeCheckbox, errormsg }) => {
  const owner = useSelector(getActiveOwners);
  const priceModel = useSelector(getGenericMasterFilteredListByKey('PriceModel'));
  const currency = useSelector(getGenericMasterFilteredListByKey('Currency'));

  const opt = (list, vKey, lKey, fmt) => (list || []).map((o) => ({ value: o[vKey], label: fmt ? fmt(o[lKey]) : o[lKey] }));

  const renderField = (fl) => {
    const err = errormsg?.[fl.name];
    let control;
    if (fl.type === 'select') {
      control = (
        <Select
          value={formData?.[fl.display] || ''}
          placeholder={'Select ' + fl.label}
          options={fl.options}
          onChange={(o) => handleChange(o, fl.name)}
          searchable={fl.searchable}
        />
      );
    } else if (fl.type === 'date') {
      control = (
        <input
          type="date"
          className={'gos-cl-input' + (err ? ' err' : '')}
          name={fl.name}
          value={formData?.[fl.name] ? String(formData[fl.name]).substring(0, 10) : ''}
          onChange={handleChangeForm}
        />
      );
    } else if (fl.type === 'money' || fl.type === 'percent') {
      control = (
        <div className="gos-qf-money">
          <span className="gos-qf-money-sym">{fl.type === 'money' ? CURRENCY : '%'}</span>
          <input
            className={'gos-cl-input' + (err ? ' err' : '')}
            type="number"
            min={0}
            step={fl.type === 'money' ? '0.01' : '1'}
            name={fl.name}
            placeholder={fl.type === 'money' ? '0.00' : '0'}
            value={formData?.[fl.name] ?? ''}
            onChange={handleChangeForm}
          />
        </div>
      );
    } else if (fl.type === 'checkbox') {
      const on = !!formData?.[fl.name];
      control = (
        <button
          type="button"
          className={'gos-qf-check' + (on ? ' on' : '')}
          onClick={() => handleChangeCheckbox({ target: { name: fl.name, checked: !on } })}
        >
          <span className={'lead-check sm' + (on ? ' on' : '')}>{on ? <FIc name="check" size={11} /> : null}</span>
          {fl.checkLabel || fl.label}
        </button>
      );
    } else if (fl.type === 'textarea') {
      control = (
        <textarea
          className={'gos-cl-input gos-cl-textarea' + (err ? ' err' : '')}
          name={fl.name}
          rows={4}
          placeholder={'Enter ' + fl.label}
          value={formData?.[fl.name] ?? ''}
          onChange={handleChangeForm}
        />
      );
    } else {
      control = (
        <input
          className={'gos-cl-input' + (err ? ' err' : '')}
          name={fl.name}
          placeholder={'Enter ' + fl.label}
          value={formData?.[fl.name] ?? ''}
          onChange={handleChangeForm}
        />
      );
    }
    return (
      <div className={'gos-cl-field' + (fl.full ? ' gos-cl-field-full' : '')} key={fl.name}>
        {fl.type === 'checkbox' ? null : (
          <label className="gos-cl-label">
            {fl.label}
            {fl.req ? <span className="gos-cl-req">*</span> : null}
          </label>
        )}
        {control}
        {err ? <span className="gos-cl-err">{err}</span> : null}
      </div>
    );
  };

  const sectionCard = (icon, title, sub, body) => (
    <div className="gos-lr-card gos-cl-sec">
      <div className="gos-cl-sec-head">
        <span className="gos-cl-sec-ic"><FIc name={icon} size={17} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="gos-cl-sec-title">{title}</div>
          <div className="gos-cl-sec-sub">{sub}</div>
        </div>
      </div>
      {body}
    </div>
  );

  const infoFields = [
    { label: 'Price Sheet Owner', name: 'priceSheetOwnerId', type: 'select', display: 'priceSheetOwner', searchable: true, options: opt(owner, 'userId', 'display_name', NameFormatter) },
    { label: 'Price Sheet Name', name: 'name', req: true },
    { label: 'Start Date', name: 'startDate', type: 'date', req: true },
    { label: 'End Date', name: 'endDate', type: 'date', req: true },
    { label: 'Active', name: 'productActive', type: 'checkbox', checkLabel: 'Mark price sheet as active' },
  ];

  const detailFields = [
    { label: 'Price Model', name: 'priceModelId', type: 'select', display: 'priceModel', options: opt(priceModel, 'id', 'displayName') },
    { label: 'Currency', name: 'currencyPriceId', type: 'select', display: 'currencyPrice', options: opt(currency, 'id', 'displayName') },
    { label: 'Discount Percent', name: 'discountPercent', type: 'percent' },
    { label: 'Discount Amount', name: 'discountAmount', type: 'money' },
  ];

  const additionalFields = [
    { label: 'Terms and Conditions', name: 'terms', type: 'textarea', full: true },
    { label: 'Description', name: 'description', type: 'textarea', full: true },
  ];

  return (
    <div className="gos-qf-body">
      {sectionCard('note', 'Price Sheet Information', 'Name, ownership & validity',
        <div className="gos-cl-sec-body">{infoFields.map(renderField)}</div>)}
      {sectionCard('tag', 'Price Details', 'Model, currency & discount',
        <div className="gos-cl-sec-body">{detailFields.map(renderField)}</div>)}
      {sectionCard('template', 'Additional Details', 'Terms & description',
        <div className="gos-cl-sec-body">{additionalFields.map(renderField)}</div>)}
    </div>
  );
};

export default PriceForm;
